import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { pricingStudioUrl } from '@/environment';
import CloseIcon from '@/images/CloseWithCircle.svg';
import { useFlag } from '@/services/featureFlags';
import { creditsToVideoMinutes, useCreditQuery } from '@/state/credits/credits';
import styled from '@emotion/styled';
import { Button, DialogContent, DialogTitle } from '@mui/material';
import { PREMIUM_AVATAR_CREATION_COST_MINUTES } from './constants';

const StyledDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        color: ${({ theme }) => theme.colors.secondary[900]};
        font-size: 24px;
        font-weight: 600;
        letter-spacing: -0.48px;
        justify-content: left;
        padding-top: 16px;
        padding-bottom: 8px;
    }
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 10px;
    right: 10px;
    color: ${({ theme }) => theme.colors.black[800]};
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        display: flex;
        flex-direction: column;
        overflow: visible;
    }
`;

const Subtitle = styled.span`
    color: ${({ theme }) => theme.colors.black[700]};
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20.8px;
    letter-spacing: -0.32px;
`;

const StyledButton = styled(Button)`
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    width: 100%;
    text-transform: none;
    border-radius: 8px;
    padding: 10px 16px;
    color: ${({ theme }) => theme.colors.secondary.main};
    margin-top: 24px;
    box-shadow: none;
    align-self: center;

    &:hover {
        box-shadow: none;
        background: ${({ theme }) => theme.colors.primary.hover};
    }
`;
const BalanceContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.black[100]};
    padding: 16px;
    border-radius: 8px;
    margin: 16px 0;
`;

const BalanceRow = styled.div`
    display: flex;

    flex-direction: column;
    padding: 8px 0;
`;

const BalanceLabel = styled.span`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.secondary[700]};
`;

const BalanceValue = styled.span`
    font-size: 16px;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.secondary.main};
`;
const HorizontalLine = styled.hr`
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.secondary[200]};
`;

interface Props {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
}
export const CreateConfirmModal = ({ open, onClose, onConfirm }: Props) => {
    const { data: credits } = useCreditQuery();
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);
    const balanceAfterPurchase = creditsToMinutesEnabled
        ? Math.round(creditsToVideoMinutes(credits!.remaining) - PREMIUM_AVATAR_CREATION_COST_MINUTES)
        : Math.round(credits!.remaining - PREMIUM_AVATAR_CREATION_COST_MINUTES * 4);

    const onExplorePlans = () => {
        window.open(pricingStudioUrl, '_blank');
    };

    if (balanceAfterPurchase < 0) {
        return (
            <BootstrapDialog
                open={open}
                onClose={onClose}
                rootstyle={{
                    padding: '24px 40px',
                    justifyContent: 'center',
                    width: '40%',
                }}>
                <CloseButton ariaLabel="close" onClick={onClose}>
                    <MaskedIcon src={CloseIcon} size="38px" />
                </CloseButton>
                <StyledDialogTitle>Upgrade needed</StyledDialogTitle>
                <StyledDialogContent>
                    <Subtitle>
                        Creating a Premium+ Avatar costs{' '}
                        {creditsToMinutesEnabled ? PREMIUM_AVATAR_CREATION_COST_MINUTES : PREMIUM_AVATAR_CREATION_COST_MINUTES * 4} credits.{' '}
                        Upgrade your plan to continue.
                    </Subtitle>
                </StyledDialogContent>
                <StyledButton variant="contained" color="primary" id="avatar-create-confirm-modal" onClick={onExplorePlans}>
                    Explore plans
                </StyledButton>
            </BootstrapDialog>
        );
    } else {
        return (
            <BootstrapDialog
                open={open}
                onClose={onClose}
                rootstyle={{
                    padding: '24px 40px',
                    justifyContent: 'center',
                    width: '40%',
                }}>
                <CloseButton ariaLabel="close" onClick={onClose}>
                    <MaskedIcon src={CloseIcon} size="38px" />
                </CloseButton>
                <StyledDialogTitle>Ready to create your Avatar?</StyledDialogTitle>
                <StyledDialogContent>
                    <Subtitle>
                        You’re about to create your Premium+ Avatar, which will cost{' '}
                        <b>
                            {creditsToMinutesEnabled
                                ? PREMIUM_AVATAR_CREATION_COST_MINUTES + ' minutes'
                                : PREMIUM_AVATAR_CREATION_COST_MINUTES * 4 + ' credits'}
                        </b>{' '}
                        from your balance.
                    </Subtitle>
                    <BalanceContainer>
                        <BalanceRow>
                            <BalanceLabel>Your current balance</BalanceLabel>
                            <BalanceValue>
                                {' '}
                                {creditsToMinutesEnabled
                                    ? Math.round(creditsToVideoMinutes(credits!.remaining)) + ' minutes'
                                    : Math.round(credits!.remaining) + ' credits'}
                            </BalanceValue>
                        </BalanceRow>
                        <HorizontalLine />
                        <BalanceRow>
                            <BalanceLabel>Your balance after this purchase</BalanceLabel>
                            <BalanceValue>
                                {creditsToMinutesEnabled ? balanceAfterPurchase + ' minutes' : balanceAfterPurchase + ' credits'}
                            </BalanceValue>
                        </BalanceRow>
                    </BalanceContainer>
                    <StyledButton variant="contained" color="primary" id="avatar-create-confirm-modal" onClick={onConfirm}>
                        Confirm
                    </StyledButton>
                </StyledDialogContent>
            </BootstrapDialog>
        );
    }
};
